<template>
    <div :class="{ 'fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center': isModal }" v-if="isVisible">
        <div
            :class="isModal ? 'relative bg-white rounded-lg w-full max-w-xl p-6 mx-4' : 'w-full max-w-md p-6 mx-auto mt-8 bg-white shadow-md rounded-lg'">

            <!-- Close Button untuk Modal -->
            <button v-if="isModal" @click="closeModal" class="absolute top-8 right-6 text-gray-500 focus:outline-none"
                :disabled="isLoading">
                <AppIcon icon="mdi:close" class="w-6 h-6" />
            </button>

            <!-- Title -->
            <h2 class="text-xl font-semibold mb-4">{{ $t('passwordChange.title') }}</h2>
            <hr class="mb-4">

            <!-- Form Input -->
            <form @submit.prevent="submitForm">
                <!-- Current Password Field -->
                <div class="mb-4">
                    <BaseInput type="password" :placeholder="$t('passwordChange.currentPassword')"
                        v-model="form.currentPassword"
                        :class="{ 'border-red-500': errors.currentPassword || backendError.currentPassword }"
                        @input="clearCurrentPasswordError" :disabled="isLoading" />
                    <div v-if="backendError.currentPassword || errors.currentPassword"
                        class="text-red-500 text-sm mt-1">
                        {{ backendError.currentPassword || $t('passwordChange.currentPasswordError') }}
                    </div>
                </div>

                <!-- New Password Field -->
                <div class="mb-4">
                    <BaseInput type="password" :placeholder="$t('passwordChange.newPassword')"
                        v-model="form.newPassword" :class="{ 'border-red-500': errors.newPassword }"
                        @input="clearNewPasswordError" :disabled="isLoading" />
                    <div v-if="errors.newPassword" class="text-red-500 text-sm mt-1">
                        {{ errors.newPassword }}
                    </div>
                </div>

                <!-- Confirm New Password Field -->
                <div class="mb-4">
                    <BaseInput type="password" :placeholder="$t('passwordChange.confirmNewPassword')"
                        v-model="form.confirmNewPassword" :class="{ 'border-red-500': errors.confirmNewPassword }"
                        @input="clearConfirmNewPasswordError" :disabled="isLoading" />
                    <div v-if="errors.confirmNewPassword" class="text-red-500 text-sm mt-1">
                        {{ errors.confirmNewPassword }}
                    </div>
                </div>

                <!-- Submit Button -->
                <button type="submit"
                    class="w-full py-2 mt-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 flex justify-center items-center"
                    :disabled="isLoading">
                    <span v-if="isLoading" class="flex items-center">
                        <AppIcon icon="mdi:loading" class="w-5 h-5 animate-spin mr-2" />
                        {{ $t('passwordChange.loading') }}
                    </span>
                    <span v-else>
                        {{ $t('passwordChange.submitButton') }}
                    </span>
                </button>
            </form>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";
import { getResponseMessage } from "@/utils/responseMapping";

export default {
    name: "ForgotPassword",
    props: {
        isModal: {
            type: Boolean,
            default: false
        },
        isVisible: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            form: {
                currentPassword: '',
                newPassword: '',
                confirmNewPassword: ''
            },
            errors: {
                currentPassword: false,
                newPassword: '',
                confirmNewPassword: ''
            },
            backendError: {
                currentPassword: ''
            },
            isLoading: false // State untuk loading
        };
    },
    methods: {
        ...mapActions("user", ["changePassword"]),

        async submitForm() {
            this.validateForm();

            if (!this.errors.currentPassword && !this.errors.newPassword && !this.errors.confirmNewPassword) {
                this.isLoading = true; // Aktifkan state loading
                try {
                    const response = await this.changePassword({
                        currentPassword: this.form.currentPassword,
                        newPassword: this.form.newPassword,
                        confirmNewPassword: this.form.confirmNewPassword
                    });

                    const successMessage = getResponseMessage(response.status, response.data.code);
                    this.$alert.toast('success', successMessage);
                    this.closeModal();
                } catch (error) {
                    const errorCode = error.response?.data?.code;
                    const errorStatus = error.response?.status;

                    if (errorCode === 1 && errorStatus === 400) {
                        this.backendError.currentPassword = this.$t('passwordChange.invalidCurrentPassword');
                        this.form.currentPassword = ''; // Reset field
                        this.errors.currentPassword = true;
                        this.$alert.toast('warning', this.$t('passwordChange.invalidCurrentPassword'));
                    } else {
                        const validationErrors = error.response?.data?.data || [];
                        validationErrors.forEach((err) => {
                            if (err.property === 'NewPassword') {
                                this.errors.newPassword = err.error.minLength || this.$t('passwordChange.newPasswordMinLength');
                            }
                            if (err.property === 'ConfirmNewPassword') {
                                this.errors.confirmNewPassword = err.error.minLength || this.$t('passwordChange.confirmPasswordMinLength');
                            }
                        });

                        this.$alert.toast('warning', this.$t('passwordChange.error'));
                    }
                } finally {
                    this.isLoading = false; // Matikan state loading
                }
            } else {
                this.$alert.toast('warning', this.$t('passwordChange.validationFailed'));
            }
        },

        closeModal() {
            this.form.currentPassword = '';
            this.form.newPassword = '';
            this.form.confirmNewPassword = '';
            this.errors.currentPassword = false;
            this.errors.newPassword = '';
            this.errors.confirmNewPassword = '';
            this.backendError.currentPassword = '';
            this.isLoading = false; // Pastikan loading di-reset
            this.$emit('close');
        },

        validateForm() {
            // Validasi untuk Current Password
            this.errors.currentPassword = !this.form.currentPassword
                ? this.$t('passwordChange.currentPasswordError')
                : '';

            // Validasi untuk New Password
            this.errors.newPassword = this.form.newPassword.length < 8
                ? this.$t('passwordChange.newPasswordMinLength')
                : '';

            // Validasi untuk Confirm New Password
            this.errors.confirmNewPassword = this.form.confirmNewPassword.length < 8
                ? this.$t('passwordChange.confirmPasswordMinLength')
                : '';

            if (this.form.newPassword !== this.form.confirmNewPassword) {
                this.errors.confirmNewPassword = this.$t('passwordChange.confirmPasswordMismatch');
            }
        },
    }
};
</script>

<style scoped>
.bg-opacity-50 {
    background-color: rgba(0, 0, 0, 0.5);
}

.border-red-500 {
    border-color: #f87171;
}

.text-red-500 {
    color: #f87171;
}

button[disabled] {
    cursor: not-allowed;
}
</style>